import { Sizes } from 'enums/sizes';
import { createContext, Dispatch, SetStateAction } from 'react';

// ============================================
// Panel types (unchanged from PanelContext)
// ============================================

export interface PanelConfig {
  title: string;
  size: Sizes;
}

export interface PanelActions {
  onSubmit: () => void;
  closeLabel: string;
  submitLabel: string;
  submitDisabled?: boolean;
  onClose?: () => void;
  onDelete?: () => void;
  deleteLabel?: string;
  deleteDisabled?: boolean;
}

// ============================================
// Dialog types (unchanged from DialogContext)
// ============================================

export type DialogConfig = BasicDialogConfig | BlankDialogConfig;

export interface BlankDialogConfig {
  size: Sizes;
}

export interface BasicDialogConfig {
  title: string;
  description?: string;
  size: Sizes;
}

export interface DialogActions {
  onSubmit: () => void;
  /** Bouton secondaire de fermeture. Omis → seul le bouton principal est affiché. */
  closeLabel?: string;
  submitLabel: string;
  submitDisabled?: boolean;
  onDelete?: () => void;
  deleteLabel?: string;
  deleteDisabled?: boolean;
}

// ============================================
// Overlay session (internal)
// ============================================

export type OverlayType = 'panel' | 'dialog' | 'blank-dialog';

export interface OverlaySession {
  id: string;
  type: OverlayType;
  config: PanelConfig | DialogConfig;
  content: JSX.Element;
  actions?: PanelActions | DialogActions;
  closing?: boolean;
}

// ============================================
// Context API (backwards-compatible)
// ============================================

export interface PanelContextValue {
  isOpen: boolean;
  openPanel: (panelConfig: PanelConfig, panelContent: JSX.Element) => void;
  closePanel: () => void;
  setPanelActions: Dispatch<SetStateAction<PanelActions | undefined>>;
  panelConfig: PanelConfig;
  panelContent: JSX.Element;
  panelActions?: PanelActions;
}

export interface DialogContextValue {
  isOpen: boolean;
  isBlank: boolean;
  openDialog: (dialogConfig: DialogConfig, dialogContent: JSX.Element) => void;
  openBlankDialog: (dialogConfig: DialogConfig, dialogContent: JSX.Element) => void;
  closeDialog: () => void;
  /** Met à jour les actions ; sessionId cible un dialog précis (sinon le plus récent). */
  setDialogActions: (actions: SetStateAction<DialogActions | undefined>, sessionId?: string) => void;
  /** Met à jour le titre ; sessionId cible un dialog précis (sinon le plus récent). */
  setDialogTitle: (title: string, sessionId?: string) => void;
  dialogConfig: DialogConfig;
  dialogContent: JSX.Element;
  dialogActions?: DialogActions;
}

export const PanelContext = createContext<PanelContextValue | undefined>(undefined);
export const DialogContext = createContext<DialogContextValue | undefined>(undefined);

/**
 * Id de la session de dialog dans laquelle un contenu est rendu. Permet à useDialog de scoper
 * setDialogActions/setDialogTitle au bon dialog (et non au plus récent, ex. une popup au-dessus).
 */
export const DialogSessionContext = createContext<string | undefined>(undefined);
